import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import './ProjectCard.css';

const ProjectCard = ({ project, onClick }) => {
  const [isActive, setIsActive] = useState(false);

  if (!project || !project.mainImage) {
    return null;
  }

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
      <Box
        className={`project-card ${isActive ? 'is-active' : ''}`}
        onClick={() => onClick(project)}
        onMouseEnter={() => setIsActive(true)}
        onMouseLeave={() => setIsActive(false)}
        onTouchStart={() => setIsActive(true)}
        onTouchEnd={() => setIsActive(false)}
        onTouchCancel={() => setIsActive(false)}
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '5/7',
          overflow: 'hidden',
          cursor: 'pointer',
          backgroundColor: '#000',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        {/* Project Image */}
        <Box
          className='project-image'
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            transition: 'transform 0.6s ease',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              transition: 'background-color 0.4s ease',
              zIndex: 1
            }
          }}>
          <Box
            component='img'
            src={project.mainImage}
            alt={project.title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'top center',
              display: 'block'
            }}
          />
        </Box>

        {/* Hover Overlay */}
        <Box
          className='project-overlay'
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: 0,
            transform: 'translateY(30px)',
            transition: 'opacity 0.4s ease, transform 0.4s ease',
            zIndex: 2
          }}>
          <Typography
            variant='h5'
            sx={{
              color: '#fff',
              fontFamily: 'Jura, sans-serif',
              fontWeight: 700,
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.35rem', lg: '1.5rem' },
              letterSpacing: '0.08em',
              mb: 1,
              textAlign: 'center',
              px: { xs: 2, sm: 2.5, md: 3 },
              lineHeight: 1.4,
              wordBreak: 'break-word'
            }}>
            {project.title}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: '#fff',
              fontFamily: 'Jura, sans-serif',
              fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem' },
              letterSpacing: '0.05em',
              textAlign: 'center',
              px: { xs: 2, sm: 2.5, md: 3 }
            }}>
            {project.location}
          </Typography>
        </Box>

        {/* Corner Lines */}
        <Box className='corner-lines-project'>
          {/* Top-left corner */}
          <Box
            className='corner-line-project top-left'
            sx={{
              position: 'absolute',
              top: 20,
              left: 20,
              opacity: 0,
              transition: 'opacity 0.4s ease',
              zIndex: 3,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: 0,
                height: '2px',
                backgroundColor: '#fff',
                transition: 'width 0.4s ease'
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '2px',
                height: 0,
                backgroundColor: '#fff',
                transition: 'height 0.4s ease'
              }
            }}
          />

          {/* Bottom-right corner */}
          <Box
            className='corner-line-project bottom-right'
            sx={{
              position: 'absolute',
              bottom: 20,
              right: 20,
              opacity: 0,
              transition: 'opacity 0.4s ease',
              zIndex: 3,
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: 0,
                height: '2px',
                backgroundColor: '#fff',
                transition: 'width 0.4s ease'
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '2px',
                height: 0,
                backgroundColor: '#fff',
                transition: 'height 0.4s ease'
              }
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectCard;
